/**
 * Connect softphone error types (Streams SDK) and human-readable troubleshooting copy.
 * Used by the connection-failed panel and softphone failure classification.
 */

export type ConnectSoftphoneErrorType =
    | 'microphone_not_shared'
    | 'ice_collection_timeout'
    | 'signalling_handshake_failure'
    | 'signalling_connection_failure'
    | 'webrtc_error'
    | 'realtime_communication_error'
    | 'user_busy_error'
    | 'unsupported_browser'
    | 'vdi_strategy_not_supported'
    | 'vdi_redir_not_supported'
    | 'other';

export interface ConnectionFailureStep {
    id: string;
    text: string;
    icon: string;
}

export interface ConnectionFailureGuideline {
    errorType: ConnectSoftphoneErrorType;
    label: string;
    typicalCause: string;
    /** Single plain-language explanation shown to the user. */
    summary: string;
    steps: ConnectionFailureStep[];
}

const CONNECT_FAILURE_GUIDELINES: Record<ConnectSoftphoneErrorType, ConnectionFailureGuideline> = {
    microphone_not_shared: {
        errorType: 'microphone_not_shared',
        label: 'Microphone not shared',
        typicalCause: 'Mic permission denied, blocked, or getUserMedia failed.',
        summary: 'Your browser blocked microphone access, so the call could not connect.',
        steps: [
            { id: 'mic', text: 'Allow Microphone Access in your browser ', icon: 'bi-mic' },
            { id: 'chrome', text: 'Use Google Chrome for the best softphone experience', icon: 'chrome' },
            { id: 'close-apps', text: 'Close other apps that are using microphone', icon: 'bi-mic-mute' },
       
        ],
    },
    ice_collection_timeout: {
        errorType: 'ice_collection_timeout',
        label: 'ICE collection timed out',
        typicalCause: 'No ICE candidates (VPN/firewall, UDP 3478/TURN blocked).',
        summary: 'Your network blocked the call from connecting. VPN or firewall software is often the cause.',
        steps: [
            { id: 'vpn', text: 'Turn off VPN; restart your device if problems continue', icon: 'bi-shield-slash' },
            { id: 'connection-test', text: 'Run Connection Test from the CCP Widget Settings', icon: 'bi-wifi' },
            { id: 'tab-focus', text: 'Keep this browser tab open and in focus during calls', icon: 'bi-pin-angle' },
        ],
    },
    signalling_handshake_failure: {
        errorType: 'signalling_handshake_failure',
        label: 'Signalling handshake failed',
        typicalCause: 'WebRTC signaling handshake failed.',
        summary: 'The call could not finish setting up. Your network or browser may be blocking the connection.',
        steps: [
            { id: 'chrome', text: 'Use Google Chrome and keep this tab in focus', icon: 'chrome' },
            { id: 'vpn', text: 'Turn off VPN or firewall software that may block WebRTC', icon: 'bi-shield-slash' },
            { id: 'sign-out', text: 'Sign out and sign back in, then go Available for your next call', icon: 'bi-box-arrow-right' },
        ],
    },
    signalling_connection_failure: {
        errorType: 'signalling_connection_failure',
        label: 'Signalling connection failed',
        typicalCause: "Can't reach signaling endpoint.",
        summary: 'We could not reach the call service. Check your internet connection before your next call.',
        steps: [
            { id: 'connection-test', text: 'Run Connection Test from the CCP Widget Settings', icon: 'bi-wifi' },
            { id: 'vpn', text: 'Turn off VPN; restart your device if problems continue', icon: 'bi-shield-slash' },
            { id: 'single-session', text: 'Only use your logged-in CCP widget session in a single browser', icon: 'bi-window' },
        ],
    },
    webrtc_error: {
        errorType: 'webrtc_error',
        label: 'WebRTC error',
        typicalCause: 'Generic browser WebRTC failure.',
        summary: 'Your browser could not establish the call connection.',
        steps: [
            { id: 'chrome', text: 'Use Google Chrome for the best softphone experience', icon: 'chrome' },
            { id: 'tab-focus', text: 'Keep this browser tab open and in focus during calls', icon: 'bi-pin-angle' },
            { id: 'sign-out', text: 'Sign out and sign back in, then go Available for your next call', icon: 'bi-box-arrow-right' },
        ],
    },
    realtime_communication_error: {
        errorType: 'realtime_communication_error',
        label: 'Real-time communication error',
        typicalCause: 'Real-time media path failed.',
        summary: 'The call audio could not be established. Check your network and try again on your next call.',
        steps: [
            { id: 'vpn', text: 'Turn off VPN; restart your device if problems continue', icon: 'bi-shield-slash' },
            { id: 'connection-test', text: 'Run Connection Test from the CCP Widget Settings', icon: 'bi-wifi' },
            { id: 'tab-focus', text: 'Keep this browser tab open and in focus during calls', icon: 'bi-pin-angle' },
        ],
    },
    user_busy_error: {
        errorType: 'user_busy_error',
        label: 'Line busy',
        typicalCause: 'Agent line reported as busy.',
        summary: 'Your line looks busy—another session or call may already be active.',
        steps: [
            { id: 'single-session', text: 'Only use your logged-in CCP widget session in a single browser', icon: 'bi-window' },
            { id: 'sign-out', text: 'Sign out and sign back in, then go Available for your next call', icon: 'bi-box-arrow-right' },
        ],
    },
    unsupported_browser: {
        errorType: 'unsupported_browser',
        label: 'Unsupported browser',
        typicalCause: 'Browser not supported for softphone.',
        summary: 'This browser is not supported for taking calls.',
        steps: [
            { id: 'chrome', text: 'Login from Google Chrome', icon: 'chrome' },
            { id: 'cookies', text: 'Allow third-party cookies in your browser settings', icon: 'bi-shield-check' },
        ],
    },
    vdi_strategy_not_supported: {
        errorType: 'vdi_strategy_not_supported',
        label: 'VDI audio not supported',
        typicalCause: 'VDI audio strategy not supported.',
        summary: 'Your virtual desktop setup is not supported for softphone calls.',
        steps: [
            { id: 'vdi', text: 'Use a physical device or a VDI setup supported by Amazon Connect softphone', icon: 'bi-pc-display' },
            { id: 'support', text: 'Contact your administrator if you must take calls from this environment', icon: 'bi-question-circle' },
        ],
    },
    vdi_redir_not_supported: {
        errorType: 'vdi_redir_not_supported',
        label: 'VDI media redirection not supported',
        typicalCause: 'VDI media redirection not supported.',
        summary: 'Your virtual desktop cannot redirect call audio.',
        steps: [
            { id: 'vdi', text: 'Use a physical device or a VDI setup supported by Amazon Connect softphone', icon: 'bi-pc-display' },
            { id: 'support', text: 'Contact your administrator if you must take calls from this environment', icon: 'bi-question-circle' },
        ],
    },
    other: {
        errorType: 'other',
        label: 'Unclassified error',
        typicalCause: 'Catch-all / unclassified softphone failure.',
        summary: 'Something went wrong while connecting the call.',
        steps: [
            { id: 'connection-test', text: 'Run Connection Test from the CCP Widget Settings', icon: 'bi-wifi' },
            { id: 'vpn', text: 'Turn off VPN; restart your device if problems continue', icon: 'bi-shield-slash' },
            { id: 'sign-out', text: 'Sign out and sign back in, then go Available for your next call', icon: 'bi-box-arrow-right' },
        ],
    },
};

const KNOWN_ERROR_TYPES = new Set<string>(Object.keys(CONNECT_FAILURE_GUIDELINES));

export function normalizeConnectSoftphoneErrorType(raw: string): ConnectSoftphoneErrorType {
    const normalized = raw
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '_')
        .replace(/-/g, '_');

    if (KNOWN_ERROR_TYPES.has(normalized)) {
        return normalized as ConnectSoftphoneErrorType;
    }

    if (normalized.includes('microphone') || normalized.includes('getusermedia') || normalized.includes('gum')) {
        return 'microphone_not_shared';
    }
    if (normalized.includes('ice_collection')) {
        return 'ice_collection_timeout';
    }
    if (normalized.includes('signalling_handshake') || normalized.includes('signaling_handshake')) {
        return 'signalling_handshake_failure';
    }
    if (normalized.includes('signalling_connection') || normalized.includes('signaling_connection')) {
        return 'signalling_connection_failure';
    }
    if (normalized.includes('realtime_communication')) {
        return 'realtime_communication_error';
    }
    if (normalized.includes('user_busy') || normalized.includes('pending_busy')) {
        return 'user_busy_error';
    }
    if (normalized.includes('unsupported_browser')) {
        return 'unsupported_browser';
    }
    if (normalized.includes('vdi_strategy')) {
        return 'vdi_strategy_not_supported';
    }
    if (normalized.includes('vdi_redir')) {
        return 'vdi_redir_not_supported';
    }
    if (normalized.includes('webrtc')) {
        return 'webrtc_error';
    }
    if (normalized.includes('ice') || normalized.includes('stun') || normalized.includes('turn')) {
        return 'ice_collection_timeout';
    }
    if (normalized.includes('signalling') || normalized.includes('signaling') || normalized.includes('realtime')) {
        return 'signalling_connection_failure';
    }

    return 'other';
}

export function getConnectionFailureGuideline(
    errorType: ConnectSoftphoneErrorType,
): ConnectionFailureGuideline {
    return CONNECT_FAILURE_GUIDELINES[errorType] ?? CONNECT_FAILURE_GUIDELINES.other;
}

export function buildConnectionFailureMessage(guideline: ConnectionFailureGuideline): string {
    const firstStep = guideline.steps[0]?.text;
    return firstStep ? `${guideline.summary} ${firstStep}` : guideline.summary;
}
